import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Eye, Receipt as ReceiptIcon } from "lucide-react";
import Ticket from "@/components/Ticket";

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

export default function Sales() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const params = { q: q || undefined };
    if (start) params.start = new Date(start).toISOString();
    if (end) {
      const d = new Date(end);
      d.setHours(23, 59, 59, 999);
      params.end = d.toISOString();
    }
    const { data } = await api.get("/sales", { params });
    setItems(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q, start, end]); // eslint-disable-line

  const view = (s) => {
    setSelected(s);
    setOpen(true);
  };

  const totals = items.reduce(
    (acc, s) => ({ total: acc.total + s.total, count: acc.count + 1 }),
    { total: 0, count: 0 }
  );

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="sales-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-clay font-semibold">Operaciones</div>
          <h1 className="font-outfit text-4xl font-bold text-stone">Historial de ventas</h1>
        </div>
        <div className="bg-white border border-warm-border rounded-xl px-5 py-3 text-right">
          <div className="text-[11px] uppercase tracking-[0.2em] text-stone/50 font-semibold">
            Total filtrado
          </div>
          <div className="font-outfit text-2xl font-bold text-stone" data-testid="sales-total-filtered">
            {formatMXN(totals.total)} <span className="text-sm text-stone/50 font-figtree font-medium">· {totals.count}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-warm-border overflow-hidden shadow-sm">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-warm-border">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone/40" />
            <Input
              placeholder="Buscar por folio..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="sales-search"
              className="pl-10 border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0"
            />
          </div>
          <Input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            data-testid="sales-start-date"
            className="w-44 border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0"
          />
          <span className="text-stone/40">→</span>
          <Input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            data-testid="sales-end-date"
            className="w-44 border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0"
          />
          {(start || end) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setStart(""); setEnd(""); }}
              className="text-stone/60"
            >
              Limpiar
            </Button>
          )}
        </div>

        <table className="w-full">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone/50">
              <th className="px-6 py-3 font-semibold">Folio</th>
              <th className="px-6 py-3 font-semibold">Fecha</th>
              <th className="px-6 py-3 font-semibold">Cajero</th>
              <th className="px-6 py-3 font-semibold">Pago</th>
              <th className="px-6 py-3 font-semibold text-center">Items</th>
              <th className="px-6 py-3 font-semibold text-right">Total</th>
              <th className="px-6 py-3 font-semibold text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-warm-border hover:bg-sand/60 transition-colors">
                <td className="px-6 py-3 font-mono text-sm font-medium text-stone">{s.folio}</td>
                <td className="px-6 py-3 text-sm text-stone/70">
                  {new Date(s.created_at).toLocaleString("es-MX")}
                </td>
                <td className="px-6 py-3 text-sm text-stone/70">{s.cashier_name}</td>
                <td className="px-6 py-3">
                  <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded bg-sand text-stone/70">
                    {s.payment_method}
                  </span>
                </td>
                <td className="px-6 py-3 text-sm text-center text-stone/70">{s.items.length}</td>
                <td className="px-6 py-3 text-right font-outfit font-semibold text-stone">{formatMXN(s.total)}</td>
                <td className="px-6 py-3 text-right">
                  <button
                    onClick={() => view(s)}
                    data-testid={`sale-view-${s.id}`}
                    className="p-2 rounded-md hover:bg-sand text-stone/60 hover:text-clay"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-stone/50">
                  <ReceiptIcon className="w-10 h-10 mx-auto mb-2 text-stone/20" />
                  No hay ventas en el rango seleccionado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && <Ticket open={open} onOpenChange={setOpen} sale={selected} />}
    </div>
  );
}
